import { ConfirmButtonGroup, SingleCheckbox } from '@healthhub/react-ui';
import type { SerializedError } from '@reduxjs/toolkit';
import type { ModalProps } from 'antd';
import { Modal, Typography } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { showErrorModal } from '@shared/components';
import { USER_PREFERENCE } from '@shared/constants/userPreferencesKey';
import { useLayoutViewContext } from '@shared/context';
import { useUpdateUserPreferencesMutation } from '@shared/services/userProfile';

const { Text, Title } = Typography;

interface Props extends ModalProps {
  handleDoLater: () => void;
  handleProceed: () => void;
  hideDoNotShowAgainCheckbox?: boolean;
}

const UpdateProfileModal = ({ handleDoLater, handleProceed, hideDoNotShowAgainCheckbox = false, ...rest }: Props) => {
  const { isSharedServiceView, isStandardView } = useLayoutViewContext();

  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);

  const [updateUserPreferences, { isLoading: isLoadingUpdateUserPreferences }] = useUpdateUserPreferencesMutation();

  const updateDoLater = async () => {
    if (isChecked) {
      try {
        await updateUserPreferences({
          [USER_PREFERENCE.MYINFO_PROMPT_DISABLED]: true,
        }).unwrap();
      } catch (err) {
        const error = err as SerializedError;
        showErrorModal({
          content: error.message,
          errorCode: error.code,
        });
      }
    }
  };

  const handleCancel = () => {
    updateDoLater();
    handleDoLater();
    Modal.destroyAll();
  };

  const handleSubmit = () => {
    updateDoLater();
    handleProceed();
    Modal.destroyAll();
  };

  const handleCheckboxChange = (event: CheckboxChangeEvent) => {
    const { checked } = event.target;
    setIsChecked(checked);
  };

  if (isSharedServiceView || isStandardView) return null;

  return (
    <Modal centered {...rest} closable={false} footer={null}>
      <div className="flex flex-col gap-4">
        <Title className="mb-0 text-center text-base font-bold">{t('updateProfile.header')}</Title>
        <Text className="text-center text-sm">{t('updateProfile.content')}</Text>
        <ConfirmButtonGroup
          cancelText={t('updateProfile.iWillDoLater')}
          className="items-center lg:flex-col [&_button]:max-w-full"
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
          isLoading={isLoadingUpdateUserPreferences}
          okText={t('updateProfile.proceed')}
        />
        {!hideDoNotShowAgainCheckbox && (
          <SingleCheckbox checked={isChecked} label={t('updateProfile.donShowAgain')} onChange={handleCheckboxChange} />
        )}
      </div>
    </Modal>
  );
};

export default UpdateProfileModal;
